/**
 * FIP List Page
 *
 * React migration for the Angular FipListComponent.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FipListTable } from './FipListTable';
import { FipEditModal } from './FipEditModal';
import { fipAPI } from '../../../services/fip-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { Alert } from '../../../components/ui/Alert';
import { DEFAULT_PAGE_SIZE, DEFAULT_CURRENT_PAGE } from '../../../constants/pagination.constants';
import type {
  FipListPageProps,
  FipRecord,
  FipPermissions,
} from '../../../types/fip-list';

const derivePermissions = (): FipPermissions => {
  try {
    const userInfoEncoded = sessionStorage.getItem('loggedUserInfo');
    if (!userInfoEncoded) {
      return { canEdit: false, isSuperAdmin: false };
    }

    const userInfo = JSON.parse(atob(userInfoEncoded));
    const menuList = userInfo.menuList || [];
    const fipMenu = menuList.find((menu: any) => menu.code === 'FIP');
    const features = fipMenu?.features || [];

    const canEdit = features.some(
      (feature: any) => feature.code?.trim() === 'FIPEDIT-BUTTON'
    );

    return {
      canEdit,
      isSuperAdmin: userInfo.role === 'superadmin',
    };
  } catch (error) {
    console.error('[FipListPage] Failed to parse permissions:', error);
    return { canEdit: false, isSuperAdmin: false };
  }
};

export const FipListPage: React.FC<FipListPageProps> = ({
  routeParams,
  queryParams,
  currentRoute,
}) => {
  console.log('[FipListPage] Mounted with props:', {
    routeParams,
    queryParams,
    currentRoute,
  });

  const permissions = useMemo(() => derivePermissions(), []);

  const [fips, setFips] = useState<FipRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFip, setSelectedFip] = useState<FipRecord | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { error, errorMessage, handleError, clearError } = useErrorHandler();

  /**
   * Fetch FIP records from backend
   */
  const fetchFips = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      const response = await fipAPI.getFips({
        limit: pageSize,
        pageno: currentPage,
      });

      const rows = response?.data?.rows ?? [];
      const enrichedRows: FipRecord[] = rows.map((rawRow: any) => {
        const isActive =
          typeof rawRow.status === 'string'
            ? rawRow.status.toLowerCase() === 'active'
            : Boolean(rawRow.status);

        return {
          ...rawRow,
          status: isActive,
          fipStatus: isActive ? 'active' : 'inactive',
        } as FipRecord;
      });

      setFips(enrichedRows);
      setTotalCount(response?.data?.count ?? 0);
    } catch (err) {
      handleError(err, { component: 'FipListPage', action: 'fetchFips' });
      setFips([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, clearError, handleError]);

  useEffect(() => {
    fetchFips();
  }, [fetchFips]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleEditRecord = useCallback((record: FipRecord) => {
    setSelectedFip(record);
    setEditModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setEditModalOpen(false);
    setSelectedFip(null);
  }, []);

  const handleUpdateStatus = useCallback(
    async (status: boolean) => {
      if (!selectedFip) {
        return;
      }

      setIsUpdating(true);
      setSuccessMessage(null);

      try {
        const response = await fipAPI.updateFipStatus({
          id: selectedFip.fipId,
          status,
        });

        if (response?.status === 'success' || response?.message) {
          setSuccessMessage(
            response?.message || 'FIP status updated successfully.'
          );
        }

        await fetchFips();
        handleCloseModal();
      } catch (err) {
        handleError(err, { component: 'FipListPage', action: 'updateFipStatus' });
      } finally {
        setIsUpdating(false);
      }
    },
    [selectedFip, fetchFips, handleCloseModal, handleError]
  );

  return (
    <div className="equal-ds-root h-full flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold text-text-primary">FIP List</h1>
        <p className="text-sm text-text-secondary mt-1">
          Explore financial information providers and manage activation status.
        </p>
      </header>

      {errorMessage && (
        <Alert variant="error">
          <div className="flex flex-col gap-1">
            <span>{errorMessage}</span>
            {error?.message && (
              <span className="text-xs opacity-80">{error.message}</span>
            )}
          </div>
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <FipListTable
        data={fips}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        isLoading={isLoading}
        canEdit={permissions.canEdit}
        isSuperAdmin={permissions.isSuperAdmin}
        onEdit={handleEditRecord}
      />

      <FipEditModal
        isOpen={editModalOpen}
        record={selectedFip}
        onClose={handleCloseModal}
        onSubmit={handleUpdateStatus}
        isSubmitting={isUpdating}
      />
    </div>
  );
};
